import { Container, Row, Col, Form, Table } from 'react-bootstrap';
import React, { useState, useMemo, ChangeEventHandler } from 'react';
import Loading from '../common/Loading';
import TooltipPreset from '../common/TooltipPreset';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons';
import { SubmissionType, Submission, SubmissionFileSimple } from '../../api/SubmissionsAPI';
import { SortIcon, sortAscending, sortDescending, sort } from './SortIcon';
import formatDate from '../../lib/formatDate';
import { UserSimple } from '../../api/UsersAPI';
import { WindowSimple } from '../Admin/Windows/windowsLib';

import './SubmissionsTable.scss';

enum SortKeys {
    USER = 'USER',
    FILENAME = 'FILENAME',
    CREATED = 'CREATED',
    DATA_PERIOD = 'DATA_PERIOD',
    TIMEFRAME = 'TIMEFRAME',
    REPORT_TYPE = 'REPORT_TYPE',
    UPLOAD_SUCCEEDED = 'UPLOAD_SUCCEEDED',
}

const submissionLabels = {
    [SubmissionType.UNSET]: '—',
    [SubmissionType.MPH_REPORT]: 'MPH Report',
    [SubmissionType.INFORMED_CONSENT]: 'Informed Consent',
};

interface IFilters {
    user: string;
    timeframe: number;
    reportType: SubmissionType;
}
const defaultFilters: IFilters = {
    user: 'null',
    timeframe: -1,
    reportType: SubmissionType.UNSET,
};
function getDefaultFilters(filters: Partial<IFilters> = {}) {
    return Object.assign({}, defaultFilters, filters);
}

interface IProps {
    submissions: Submission[] | null;
    uniqueUsers: UserSimple[];
    uniqueWindows: WindowSimple[];
}
export default function SubmissionsTable({ submissions, uniqueUsers, uniqueWindows }: IProps) {
    /** true = ascending */
    const [ascending, setAscending] = useState(true);
    const [sortKey, setSortKey] = useState<SortKeys>();
    const [filters, setFilters] = useState(getDefaultFilters);
    const setFilter =
        (filterKey: keyof IFilters): ChangeEventHandler<HTMLSelectElement> =>
        (event) => {
            // * copy value outside of setFilters, lest we use event.persist()
            const filterValue = event.currentTarget.value;
            setFilters((_) => ({
                ..._,
                [filterKey]: filterValue,
            }));
        };

    const sortColumn = (key: SortKeys) => {
        setAscending(sortKey === key ? !ascending : true);
        setSortKey(key);
    };

    const filteredData = useMemo(
        () =>
            submissions?.filter((_) => {
                // ! eqeqeq disabled here because derived filter types are converted
                // ! to string equivalents from coming from setFilter event
                // eslint-disable-next-line eqeqeq
                if (filters.user != 'null' && filters.user != _.firstName + ' ' + _.lastName) {
                    return false;
                }
                // eslint-disable-next-line eqeqeq
                if (filters.timeframe != -1 && filters.timeframe != _.window.windowId) {
                    return false;
                }
                // eslint-disable-next-line eqeqeq
                if (filters.reportType != SubmissionType.UNSET && filters.reportType != _.submissionType) {
                    return false;
                }
                return true;
            }) ?? [],
        [filters, submissions],
    );

    const sortedData = useMemo(() => {
        return filteredData.sort((a, b) => {
            switch (sortKey) {
                case SortKeys.USER:
                    return sort(ascending, a.firstName, b.firstName);
                case SortKeys.FILENAME:
                    return (
                        sort(ascending, a.submissionFiles?.length, b.submissionFiles?.length) ||
                        sort(
                            ascending,
                            sortFiles(ascending, a.submissionFiles).join(''),
                            sortFiles(ascending, b.submissionFiles).join(''),
                        )
                    );
                case SortKeys.CREATED:
                    return sort(ascending, a.created, b.created);
                case SortKeys.DATA_PERIOD:
                    return sort(ascending, a.window.windowStart, b.window.windowStart);
                case SortKeys.TIMEFRAME:
                    return sort(ascending, a.window.windowStart, b.window.windowStart);
                case SortKeys.REPORT_TYPE:
                    return sort(ascending, a.submissionType, b.submissionType);
                case SortKeys.UPLOAD_SUCCEEDED:
                    return sort(ascending, Number(a.submissionComplete), Number(b.submissionComplete));
            }
            return 0;
        });
    }, [ascending, filteredData, sortKey]);

    return (
        <Container>
            <Row>
                <Col className="mt-2">
                    {submissions === null ? (
                        <Loading />
                    ) : (
                        <div>
                            <h3 className="mb-3">
                                Submissions
                                <TooltipPreset text="Only files that were successfully submitted for hashing will appear on this list">
                                    <FontAwesomeIcon className="ml-1 h6" icon={faQuestionCircle} />
                                </TooltipPreset>
                            </h3>
                            <Row>
                                <Col className="d-flex">
                                    <Form.Group className="mr-2">
                                        <Form.Label className="mr-2">Users:</Form.Label>
                                        <select onChange={setFilter('user')}>
                                            <option value="null">All Users</option>
                                            {uniqueUsers.map((user, i) => {
                                                return (
                                                    <option key={i} value={user.firstName + ' ' + user.lastName}>
                                                        {user.firstName + ' ' + user.lastName}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </Form.Group>
                                    <Form.Group className="mr-2">
                                        <Form.Label className="mr-2">Timeframes:</Form.Label>
                                        <select onChange={setFilter('timeframe')}>
                                            <option value={-1}>All Timeframes</option>
                                            {uniqueWindows.map((window, index) => {
                                                return (
                                                    <option key={index} value={window.windowId}>
                                                        {formatDate(new Date(window.windowStart!)) +
                                                            ' - ' +
                                                            formatDate(new Date(window.windowEnd!))}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </Form.Group>
                                    <Form.Group>
                                        <Form.Label className="mr-2">Report Types:</Form.Label>
                                        <select onChange={setFilter('reportType')}>
                                            <option value={SubmissionType.UNSET}>All Report Types</option>
                                            <option value={SubmissionType.MPH_REPORT}>MPH Report</option>
                                            <option value={SubmissionType.INFORMED_CONSENT}>Informed Consent</option>
                                        </select>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Table striped bordered size="sm" className="text-center">
                                <thead>
                                    <tr>
                                        <th onClick={() => sortColumn(SortKeys.USER)}>
                                            User
                                            <SortIcon ascending={ascending} sorting={sortKey === SortKeys.USER} />
                                        </th>
                                        <th onClick={() => sortColumn(SortKeys.FILENAME)}>
                                            File Name
                                            <SortIcon ascending={ascending} sorting={sortKey === SortKeys.FILENAME} />
                                        </th>
                                        <th onClick={() => sortColumn(SortKeys.REPORT_TYPE)}>
                                            Report Type
                                            <SortIcon
                                                ascending={ascending}
                                                sorting={sortKey === SortKeys.REPORT_TYPE}
                                            />
                                        </th>
                                        <th onClick={() => sortColumn(SortKeys.CREATED)}>
                                            Submission Date
                                            <SortIcon ascending={ascending} sorting={sortKey === SortKeys.CREATED} />
                                        </th>
                                        <th onClick={() => sortColumn(SortKeys.DATA_PERIOD)}>
                                            Data Period
                                            <SortIcon
                                                ascending={ascending}
                                                sorting={sortKey === SortKeys.DATA_PERIOD}
                                            />
                                        </th>
                                        <th onClick={() => sortColumn(SortKeys.TIMEFRAME)}>
                                            <div className="d-flex flex-row align-items-center justify-content-center">
                                                Timeframe
                                                <SortIcon
                                                    ascending={ascending}
                                                    sorting={sortKey === SortKeys.TIMEFRAME}
                                                />
                                            </div>
                                        </th>
                                        {/* <th>Rows</th> */}
                                        <th onClick={() => sortColumn(SortKeys.UPLOAD_SUCCEEDED)}>
                                            Upload Succeeded
                                            <SortIcon
                                                ascending={ascending}
                                                sorting={sortKey === SortKeys.UPLOAD_SUCCEEDED}
                                            />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {submissions.length > 0 ? (
                                        sortedData.map((_) =>
                                            _.submissionFiles?.map((__, i) => (
                                                <tr key={i}>
                                                    <td>{`${_.firstName} ${_.lastName}`.trim() || 'N/A'}</td>
                                                    <td>{__.originalFileName}</td>
                                                    <td>{submissionLabels[_.submissionType as SubmissionType]}</td>
                                                    <td>{formatDate(new Date(_.created), true)}</td>
                                                    <td>
                                                        <div className="d-flex align-items-center justify-content-center">
                                                            {formatDate(
                                                                new Date(_.window.dataCollectedStart!.replace('Z', '')),
                                                            ) +
                                                                ' - ' +
                                                                formatDate(_.window.dataCollectedEnd!.replace('Z', ''))}
                                                        </div>
                                                    </td>
                                                    {/* <td></td> */}
                                                    <td>
                                                        {_.window.name ? <div>{_.window.name}</div> : null}
                                                        <div>
                                                            {formatDate(new Date(_.window.windowStart!)) +
                                                                ' - ' +
                                                                formatDate(new Date(_.window.windowEnd!))}
                                                        </div>
                                                    </td>
                                                    {/* <td>{_.rowsSubmitted}</td> */}
                                                    <td>{__.uploadSucceed ? 'Yes' : 'No'}</td>
                                                </tr>
                                            )),
                                        )
                                    ) : (
                                        <tr>
                                            <td colSpan={6}>No Submissions</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Col>
            </Row>
        </Container>
    );
}

function sortFiles(ascending: boolean, files: SubmissionFileSimple[] | null | undefined) {
    const _sort = ascending ? sortAscending : sortDescending;
    return (
        files?.sort((a, b) => {
            return _sort(a.originalFileName!, b.originalFileName!);
        }) ?? []
    );
}
